import React from 'react';
import moment from 'moment';
import { useQueries } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { descend, flatten, isEmpty, pickBy, pluck, prop, sortWith } from 'ramda';
import { questionnairesQuery } from 'pages/PatientRecords/PatientRecords';
import Skeleton from 'react-loading-skeleton';
import Graphic from 'components/Graphic/Graphic';
import Patient from 'components/Patient/Patient';
import { FilterType, SearchFilter, SearchFilterTag } from 'components/SearchFilter/SearchFilter';
import { motion } from 'framer-motion';
import { visitTypes, VISIT_STATUS, visitIconsAndColors } from '../../context/visitContext';
import { paths } from '../../routes';
import { Patient as PatientType, QuestionAnswers } from '../../types/types';
import { useAppContext } from '../../context/appContext';
import Icon from '../../components/Icon/Icon';
import { US_STATES, formatPatientAgeAndGender, isRole } from '../../utils/utils';

function MedicalVisits() {
  const [{ user, patients }] = useAppContext();
  const [search, setSearch] = React.useState<FilterType | undefined>(undefined);
  const [showFilterView, setShowFilterView] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);

  const canFetchVisit =
    (!isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') && params.get('status') === 'pending') ||
    params.get('status') === 'completed' ||
    params.get('status') === 'complete';

  const questionnairesQueries = useQueries({
    queries: patients.map(fndPatient => {
      return {
        ...questionnairesQuery({
          patientId: fndPatient.id,
          status:
            params.get('status') === 'completed' || params.get('status') === 'complete'
              ? VISIT_STATUS.COMPLETE
              : VISIT_STATUS.UNANSWERED,
        }),
        enabled: !!(fndPatient.id && canFetchVisit),
      };
    }),
  });

  const isLoading = questionnairesQueries.some(fndQuery => fndQuery.isLoading);

  const visits = React.useMemo(() => {
    let fVisits = flatten(pluck('data', questionnairesQueries)) as QuestionAnswers[];

    fVisits = sortWith([descend(prop('createdAt'))], fVisits);

    if (search) {
      fVisits = fVisits.filter(fndVisit => {
        let isMatch = true;

        if (search.visit) {
          isMatch = isMatch && fndVisit.type === search.visit;
        }

        if (search.month) {
          isMatch =
            isMatch && moment(fndVisit.createdAt, 'YYYY-MM-DD').format('MMMM') === search.month;
        }

        if (search.name) {
          const findPatient = patients.find(fndPatient => fndPatient.id === fndVisit.patientUserId);
          isMatch =
            isMatch &&
            Boolean(
              findPatient &&
                (`${findPatient.firstName} ${findPatient.lastName}`
                  .toLowerCase()
                  .includes(search.name.toLowerCase()) ||
                  `${findPatient.lastName} ${findPatient.firstName}`
                    .toLowerCase()
                    .includes(search.name.toLowerCase())),
            );
        }

        if (search.gender) {
          const findPatient = patients.find(fndPatient => fndPatient.id === fndVisit.patientUserId);
          isMatch =
            isMatch && Boolean(findPatient && findPatient.patientDetails.gender === search.gender);
        }

        if (search.state) {
          const findPatient = patients.find(fndPatient => fndPatient.id === fndVisit.patientUserId);
          isMatch = isMatch && Boolean(findPatient && findPatient.state === search.state);
        }

        if (search.provider) {
          const findPatient = patients.find(fndPatient => fndPatient.id === fndVisit.patientUserId);
          isMatch =
            isMatch &&
            (`${findPatient?.assignedProvider?.firstName} ${findPatient?.assignedProvider?.lastName}`
              .toLowerCase()
              .includes(search.provider.toLowerCase()) ||
              `${findPatient?.assignedProvider?.lastName} ${findPatient?.assignedProvider?.firstName}`
                .toLowerCase()
                .includes(search.provider.toLowerCase()));
        }

        return isMatch;
      });
    }

    return fVisits;
  }, [patients, questionnairesQueries, search]);

  React.useEffect(() => {
    if (!canFetchVisit) {
      navigate(paths.HOME_URL_PATH);
    }
  }, [canFetchVisit, navigate]);

  const handleVisitClicked = (visit: QuestionAnswers) => {
    const type = visit.type.toLocaleLowerCase();
    const from = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
    const tabId = '';

    navigate(
      `${paths.VISIT_RECORDS_URL_PATH.replace(':visitId', visit.id)}${tabId}?type=${type}&patient=${
        visit.patientUserId
      }&from=${from}`,
    );
  };

  const handleFilterClear = (key: string) => {
    if (!search) {
      return;
    }

    const fndSearch = {
      ...search,
      [key]: '',
    };

    if (Object.values(fndSearch).every(value => isEmpty(value))) {
      setSearch(undefined);
      return;
    }

    setSearch(fndSearch);
  };

  const getVisitTitle = (type: string) => {
    const findVisit = visitTypes.find(fndVisit => fndVisit.type === type.toLocaleUpperCase());
    if (!findVisit) return type;
    return `${findVisit.name}`;
  };

  const getVisitPatientName = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return `${findPatient?.firstName} ${findPatient?.lastName}`;
  };

  const getVisitPatientSignedAvatar = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return findPatient?.profilePicture || 'image_patient_avatar_placeholder.png';
  };

  const getVisitPatientDOBAndGender = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return findPatient ? formatPatientAgeAndGender(findPatient) : '-';
  };

  const getVisitPatientState = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return US_STATES.find(state => state.value === findPatient?.state)?.label || '-';
  };

  return (
    <div className="relative w-full px-4 pt-4 laptop:pb-8 laptop:w-3/4 mx-auto tablet:px-0">
      <div className="flex flex-row justify-between items-center mb-5">
        <div className="text-neutral-500 text-xs uppercase font-medium shrink-0">
          {isLoading ? (
            <Skeleton width={125} height={24} className="block rounded-full" duration={1.4} />
          ) : params.get('status') === 'completed' || params.get('status') === 'complete' ? (
            !isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') ? (
              'COMPLETED VISITS'
            ) : (
              'VISITS'
            )
          ) : (
            'PENDING VISITS'
          )}
        </div>

        <div className="relative w-full tablet:w-[80%] flex flex-row justify-end">
          {isLoading ? (
            <Skeleton width={90} height={24} className="block rounded-full" duration={1.4} />
          ) : (
            <>
              <motion.div
                layout
                initial={false}
                animate={showFilterView ? 'open' : 'closed'}
                variants={{
                  open: { opacity: 1, display: 'block', position: 'absolute' },
                  closed: { opacity: 0, display: 'none' },
                }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="w-full"
              >
                <SearchFilter
                  fields={
                    user && !isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN')
                      ? ['name', 'state', 'month', 'visit']
                      : ['name', 'state', 'provider', 'month']
                  }
                  onSearchViewClose={() => setShowFilterView(!showFilterView)}
                  onFilter={fndSearch => {
                    const searchValues = pickBy(
                      value => Boolean(value && !isEmpty(value)),
                      fndSearch,
                    ) as FilterType;
                    setSearch(searchValues);
                  }}
                />
              </motion.div>

              {search && !showFilterView ? (
                <div className="flex flex-row gap-2 mr-5 flex-wrap">
                  {Object.keys(search)
                    .filter(key => !isEmpty((search as any)[key]))
                    .map(key => (
                      <SearchFilterTag
                        key={key}
                        tag={key}
                        value={(search as any)[key]}
                        onTagClear={handleFilterClear}
                      />
                    ))}
                </div>
              ) : null}

              <motion.button
                initial={false}
                animate={showFilterView ? 'open' : 'closed'}
                onClick={() => setShowFilterView(!showFilterView)}
                variants={{
                  open: { opacity: 0, zIndex: -1 },
                  closed: { opacity: 1, zIndex: 10 },
                }}
                transition={{ duration: 0.5, ease: 'easeIn' }}
                className="bg-white border text-xs rounded-full py-[5px] px-5 inline-flex items-center 
                flex-shrink-0 hover:bg-river-black hover:border-bg-river-black hover:text-white group"
              >
                <Icon
                  id="iconFilter"
                  className="w-4 text-river-black mr-2 group-hover:text-white"
                />
                Filters
              </motion.button>
            </>
          )}
        </div>
      </div>

      <div className="space-y-5">
        {isLoading ? (
          <div className="w-full flex flex-col space-y-4">
            {new Array(4).fill(null).map((_, index) => (
              <Skeleton
                key={index}
                width="100%"
                height={53}
                className="block rounded-full"
                duration={1.4}
              />
            ))}
          </div>
        ) : visits.length === 0 ? (
          <div className="flex flex-col justify-center items-center mt-7 tablet:mt-7">
            {params.get('status') === 'completed' || params.get('status') === 'complete' ? (
              <>
                <Graphic id="graphicNoPendingVisit" className="h-[120px] tablet:h-[130px]" />
                <div className="text-center mt-3">
                  <p className="font-medium">You have no visits completed</p>
                  <p className="text-neutral-500 text-xs">
                    See all the visits you’ve completed in one place
                  </p>
                </div>
              </>
            ) : (
              <>
                <Graphic id="graphicNoPendingVisit" className="h-[120px] tablet:h-[130px]" />
                <div className="text-center mt-3">
                  <p className="font-medium">You’re all caught up on your visits</p>
                  <p className="text-neutral-500 text-xs">See all pending visits in one place</p>
                </div>
              </>
            )}
          </div>
        ) : (
          visits.map(visit => (
            <div
              key={visit.id}
              role="presentation"
              onClick={() => handleVisitClicked(visit)}
              className="flex cursor-pointer justify-between laptop:grid laptop:grid-cols-7 py-1 items-center rounded-full hover:bg-[#F6FAFF]"
            >
              <div className="laptop:col-span-2 flex items-center space-x-6 w-full">
                <div
                  style={{
                    backgroundColor:
                      visitIconsAndColors[visit.type.toLocaleLowerCase()]?.bgColor || '#FFE0E3',
                  }}
                  className="w-[50px] h-[50px] rounded-full flex items-center justify-center shrink-0"
                >
                  <Icon
                    className={`icon-visit icon-${visit.type.toLocaleLowerCase()}-visit`}
                    id={visitIconsAndColors[visit.type.toLocaleLowerCase()]?.icon || ''}
                    style={{
                      color: visitIconsAndColors[visit.type.toLocaleLowerCase()]?.color || '',
                    }}
                  />
                </div>
                <div className="w-full flex flex-col">
                  <div>
                    {getVisitTitle(visit.type)}
                    <div className="text-[13px] text-zinc-400 -mt-1 font-normal hidden laptop:flex">
                      On: {moment(visit.createdAt, 'YYYY-MM-DD').format('DD MMM YYYY')}
                    </div>
                  </div>
                  <div className="flex flex-flex justify-between w-full laptop:hidden items-baseline">
                    <div className="text-sm inline-flex">
                      {getVisitPatientName(visit.patientUserId)}
                    </div>

                    <div className="items-center flex">
                      <Icon
                        id="iconArrowForward"
                        className="w-4 text-[#737373] group-hover:text-river-black"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full laptop:col-span-5 hidden laptop:flex">
                <Patient
                  key={visit.patientUserId}
                  avatar={getVisitPatientSignedAvatar(visit.patientUserId)}
                  avatarBgColor="#DFE5E7"
                  name={getVisitPatientName(visit.patientUserId)}
                  about={getVisitPatientDOBAndGender(visit.patientUserId)}
                  state={getVisitPatientState(visit.patientUserId)}
                  onPatientClick={() => false}
                  isLoadingPatient={false}
                  showArrowNext
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default MedicalVisits;
